import maplibregl from "maplibre-gl";

export function addGeoJSONLayers(
  map,
  geojsonData,
  showLines,
  visualizationSettings
) {
  try {
    // Add source if it doesn't exist
    if (!map.getSource("gpx-tracks")) {
      console.log("Creating new source");
      map.addSource("gpx-tracks", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: geojsonData,
        },
      });
    }

    // ✅ Wait until the source is ready
    const onSourceReady = (e) => {
      if (e.sourceId !== "gpx-tracks" || !map.getSource("gpx-tracks")) {
        console.log("Source not ready yet");
        return;
      }

      console.log("Source is ready, adding layers");

      try {
        // 🟢 Uphill
        if (!map.getLayer("line-uphill")) {
          console.log("Adding uphill layer");
          map.addLayer({
            id: "line-uphill",
            type: "line",
            source: "gpx-tracks",
            filter: ["==", ["get", "direction"], "up"],
            layout: {
              visibility: showLines ? "visible" : "none",
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#000000",
              "line-width": 3,
              "line-opacity": 0.9,
            },
          });
        }

        // 🔴 Downhill
        if (!map.getLayer("line-downhill")) {
          console.log("Adding downhill layer");
          map.addLayer({
            id: "line-downhill",
            type: "line",
            source: "gpx-tracks",
            filter: ["==", ["get", "direction"], "down"],
            layout: {
              visibility: showLines ? "visible" : "none",
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#f44336",
              "line-width": 3,
              "line-opacity": 0.9,
            },
          });
        }

        // 🟦 Longest Run
        if (!map.getLayer("longest-run")) {
          console.log("Adding longest run layer");
          map.addLayer({
            id: "longest-run",
            type: "line",
            source: "gpx-tracks",
            filter: ["==", ["get", "direction"], "down"],
            layout: {
              visibility: "none",
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#005fcc",
              "line-width": 5,
              "line-opacity": 1,
            },
          });
        }

        // Update layer visibility
        if (map.getLayer("line-uphill")) {
          console.log("Setting uphill visibility:", showLines);
          map.setLayoutProperty(
            "line-uphill",
            "visibility",
            showLines ? "visible" : "none"
          );
        }
        if (map.getLayer("line-downhill")) {
          console.log("Setting downhill visibility:", showLines);
          map.setLayoutProperty(
            "line-downhill",
            "visibility",
            showLines ? "visible" : "none"
          );
        }

        console.log("All layers added and visibility updated");
        fitBounds(map, geojsonData, visualizationSettings);
      } catch (error) {
        console.error("Error adding layers:", error);
      } finally {
        map.off("sourcedata", onSourceReady);
      }
    };

    map.on("sourcedata", onSourceReady);
  } catch (error) {
    console.error("Error in addGeoJSONLayers:", error);
  }
}

export function fitBounds(map, geojsonData, visualizationSettings) {
  if (geojsonData.length > 0) {
    const geojsonCoords = geojsonData.flatMap((f) => {
      if (!f.geometry || !f.geometry.coordinates) return [];
      if (f.geometry.type === "Point") return [f.geometry.coordinates];
      if (f.geometry.type === "LineString") return f.geometry.coordinates;
      return [];
    });

    if (geojsonCoords.length > 0) {
      try {
        const isMobile = window.innerWidth < 800;
        const bounds = geojsonCoords.reduce(
          (b, coord) => b.extend(coord),
          new maplibregl.LngLatBounds(geojsonCoords[0], geojsonCoords[0])
        );

        // Temporarily disable terrain
        const currentTerrain = map.getTerrain();
        if (currentTerrain) {
          console.log("Temporarily disabling terrain for bounds fitting");
          map.setTerrain(null);
        }

        // Reset view before fitting
        map.setPitch(0);
        map.setBearing(0);

        // Fit bounds to show all content
        map.fitBounds(bounds, {
          padding: 40,
          duration: 0,
        });

        // Shift the center up by adjusting the latitude
        if (currentTerrain) {
          const latSpan = bounds.getNorth() - bounds.getSouth();
          const centerOffset = isMobile ? latSpan * 0.6 : latSpan * 0.2;
          const currentCenter = map.getCenter();
          const newCenter = new maplibregl.LngLat(
            currentCenter.lng,
            currentCenter.lat + centerOffset
          );
          map.setCenter(newCenter);
        }

        // Re-enable terrain if it was enabled
        if (currentTerrain) {
          console.log("Re-enabling terrain");
          map.setTerrain(currentTerrain);

          map.setPitch(visualizationSettings.pitch);
          map.setBearing(visualizationSettings.bearing);
          const zoomToUse =
            isMobile && visualizationSettings["zoom-mobile"]
              ? visualizationSettings["zoom-mobile"]
              : visualizationSettings.zoom;
          map.setZoom(zoomToUse);
        }
      } catch (error) {
        console.error("Error fitting bounds:", error);
      }
    }
  }
}
